/**
 * Utility functions related to color operations
 */

interface ColorRgb {
    red: number;
    green: number;
    blue: number;
}

interface ColorHsb {
    hue: number;
    saturation: number;
    brightness: number;
}

export default class Color {
    /* convert colors of type "rgb(255,255,255)" to hex values like #ffffff */
    static number2hex(x: string|number) {
        return ("0" + parseInt(<string>x, 10).toString(16)).slice(-2);
    }

    /**
     *
     * @param rgb ("rgb(2,255,40)"), for IE the format is "rgba(23,45,345,1)"
     * @returns {string}
     */
    static rgbString2hex(rgb: string): string {
        if (rgb.startsWith('rgb')) {
            let [r, g, b] = rgb.replace(/^rgba?\(/, "").replace(/\)$/, "").split(",").map(v => parseInt(v, 10));

            return "#" + this.number2hex(r) + this.number2hex(g) + this.number2hex(b);
        }

        return rgb;
    }
    static rgb2hex(rgb: ColorRgb): string {
        return "#" + this.number2hex(rgb.red + "") + this.number2hex(rgb.green + "") + this.number2hex(rgb.blue + "");
    }
    /**
     * converts colors of type #abcd09 to array like [100, 50, 30] (rgb)
     * @param arr
     */
    static hex2array(hex: string): number[] {
        hex = hex.replace('#', '');

        let r = hex[0] + hex[1];
        let g = hex[2] + hex[3];
        let b = hex[4] + hex[5];

        return  [ parseInt(r, 16), parseInt(g, 16), parseInt(b, 16) ];
    }
    static hex2rgb(hex: string): ColorRgb {
        let data = this.hex2array(hex);

        return  {
            red: data[0],
            green: data[1],
            blue: data[2]
        };
    }
    /**
     * converts colors of type [100, 50, 30] (rgb) to #abcd09
     * @param arr
     */
    static array2hex(arr: (string|number)[]): string {
        let r = parseInt(<string>arr[0], 10).toString(16);
        let g = parseInt(<string>arr[1], 10).toString(16);
        let b = parseInt(<string>arr[2], 10).toString(16);

        r = r.length === 1 ? '0' + r : r;
        g = g.length === 1 ? '0' + g : g;
        b = b.length === 1 ? '0' + b : b;

        return '#' + r + g + b;
    }
    static rgb2hsb(rgb: ColorRgb): ColorHsb {
        let hsb: ColorHsb = { hue: 0, saturation: 0, brightness: 0};
        let min = Math.min(rgb.red, rgb.green, rgb.blue);
        let max = Math.max(rgb.red, rgb.green, rgb.blue);
        let delta = max - min;

        hsb.brightness = max;
        hsb.saturation = max !== 0 ? 255 * delta / max : 0;

        if ( hsb.saturation !== 0 ) {
            if ( rgb.red === max ) {
                hsb.hue = (rgb.green - rgb.blue) / delta;
            } else if ( rgb.green === max ) {
                hsb.hue = 2 + (rgb.blue - rgb.red) / delta;
            } else {
                hsb.hue = 4 + (rgb.red - rgb.green) / delta;
            }
        } else {
            hsb.hue = -1;
        }

        hsb.hue *= 60;
        if ( hsb.hue < 0 ) {
            hsb.hue += 360;
        }

        hsb.saturation = hsb.saturation * 100 / 255;
        hsb.brightness = hsb.brightness * 100 / 255;
        return hsb;
    }
    static hsb2rgb(hsb: ColorHsb): ColorRgb {
        let rgb: ColorRgb = {} as ColorRgb;
        let h = Math.round(hsb.hue);
        let s = Math.round(hsb.saturation * 255 / 100);
        let v = Math.round(hsb.brightness * 255 / 100);

        if (s === 0) {
            rgb.red = rgb.green = rgb.blue = v;
        } else {
            let t1 = v;
            let t2 = (255 - s) * v / 255;
            let t3 = (t1 - t2) * (h % 60) / 60;

            if ( h === 360 ) {
                h = 0;
            }

            if ( h < 60 ) {
                rgb.red = t1;
                rgb.blue = t2;
                rgb.green = t2 + t3;
            } else if ( h < 120 ) {
                rgb.green = t1;
                rgb.blue = t2;
                rgb.red = t1 - t3;
            } else if ( h < 180 ) {
                rgb.green = t1;
                rgb.red = t2;
                rgb.blue = t2 + t3;
            } else if ( h < 240 ) {
                rgb.blue = t1;
                rgb.red = t2;
                rgb.green = t1 - t3;
            } else if ( h < 300 ) {
                rgb.blue = t1;
                rgb.green = t2;
                rgb.red = t2 + t3;
            } else if ( h < 360 ) {
                rgb.red = t1;
                rgb.green = t2;
                rgb.blue = t1 - t3;
            } else {
                rgb.red = 0;
                rgb.green = 0;
                rgb.blue = 0;
            }
        }

        return {
            red: Math.round(rgb.red),
            green: Math.round(rgb.green),
            blue: Math.round(rgb.blue)
        };
    }

    /**
     * Computes if a color is perceived as dark or bright - between 0 (dark) and 1 (bright)
     */
    static computePerceivedLightness(color: ColorRgb) {
        /*@see http://stackoverflow.com/questions/12043187/how-to-check-if-hex-color-is-too-black/12043228#12043228 */
        let lightness = (0.2126 * color.red + 0.7152 * color.green + 0.0722 * color.blue) / 255; // per ITU-R BT.709

        return lightness;
    }
};



/** WEBPACK FOOTER **
 ** ./build/js/~/tslint-loader!./source/cdn/document_root/s/diy/util/color.ts
 **/